import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    createBatchedParamsArrayFromIds,
    creatorbase,
    creatorbaseQueries,
    TiktokUser,
    TiktokUserImage,
    TiktokUserStatsCache,
    tiktokQueries,
} from '@round/api';
import { useAllPosts, type AllPostsConfig as Config } from '@round/creatorbase-helpers/api-hooks';
import {
    useTikTokUserImagesListQueries,
    useTikTokUsersListQueries,
    useTikTokUserStatsCacheListQueries,
} from '@round/platforms/tiktok';
import { isNumber } from 'utility/utility';
import uniq from 'lodash/uniq';
import { getProjectDetailsPostsParams } from './getProjectDetailsPostsParams';

export type TiktokPostsTableRow = creatorbase.TiktokPost & {
    user?: TiktokUser | null;
    userStatsCache?: TiktokUserStatsCache | null;
    image?: TiktokUserImage | null;
};

const ACCOUNTS_BATCH_SIZE = 50;

const buildUserBatchParams = (userIds: number[]) =>
    createBatchedParamsArrayFromIds(userIds, (ids) => ({ id: ids.join(',') }), ACCOUNTS_BATCH_SIZE);

const buildUserImageBatchParams = (userIds: number[]) =>
    createBatchedParamsArrayFromIds(userIds, (ids) => ({ ids }), ACCOUNTS_BATCH_SIZE);

const buildUserStatsCacheBatchParams = (userIds: number[]) =>
    createBatchedParamsArrayFromIds(userIds, (ids) => ({ user_id: ids }), ACCOUNTS_BATCH_SIZE);

const getTiktokUserIds = (posts: creatorbase.TiktokPost[]) =>
    uniq(posts.map((post) => post.tiktok_details?.account_id).filter(isNumber));

const assembleRows = (
    posts: creatorbase.TiktokPost[],
    usersById: Map<number, TiktokUser>,
    imagesByUserId: Map<number, TiktokUserImage>,
    statsByUserId: Map<number, TiktokUserStatsCache>
): TiktokPostsTableRow[] =>
    posts.map((post) => {
        const accountId = post.tiktok_details?.account_id;
        const user = isNumber(accountId) ? usersById.get(accountId) ?? null : null;
        const userStatsCache = isNumber(accountId) ? statsByUserId.get(accountId) ?? null : null;
        const image = isNumber(accountId) ? imagesByUserId.get(accountId) ?? null : null;

        return { ...post, user, userStatsCache, image };
    });

export default function useProjectDetailsTikTokPosts(campaignId: number, config: Config = { enabled: true }) {
    const postsQuery = useAllPosts(getProjectDetailsPostsParams(campaignId), config);
    const posts = postsQuery.data as creatorbase.TiktokPost[];

    const tiktokUserIds = getTiktokUserIds(posts);

    const usersData = useTikTokUsersListQueries(
        buildUserBatchParams(tiktokUserIds).map((params) => ({
            params,
            config: { enabled: tiktokUserIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(results.flatMap((result) => result.data?.results ?? []).map((user) => [user.id, user])),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    const userImagesData = useTikTokUserImagesListQueries(
        buildUserImageBatchParams(tiktokUserIds).map((params) => ({
            params,
            config: { enabled: tiktokUserIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(results.flatMap((result) => result.data ?? []).map((image) => [image.user_id, image])),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    const userStatsCacheData = useTikTokUserStatsCacheListQueries(
        buildUserStatsCacheBatchParams(tiktokUserIds).map((params) => ({
            params,
            config: { enabled: tiktokUserIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((stats) => [stats.user_id, stats])
                ),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    return {
        data: assembleRows(posts, usersData.byId, userImagesData.byId, userStatsCacheData.byId),
        isAccountDataLoading: usersData.isLoading || userImagesData.isLoading || userStatsCacheData.isLoading,
        hasError: postsQuery.hasError,
        isLoading: postsQuery.isLoading,
        refresh: postsQuery.refresh,
    };
}

// Imperative twin of the hook above: read-or-fetch a campaign's enriched posts
// straight from the cache. Use it from event handlers (e.g. copy-to-clipboard)
// where a live subscription isn't wanted. Shares params/keys with the hook, so a
// campaign already rendered by the table is a pure cache hit.
export function useEnsureProjectDetailsTiktokPosts() {
    const queryClient = useQueryClient();

    return useCallback(
        async (campaignId: number): Promise<TiktokPostsTableRow[]> => {
            const posts = (await queryClient.ensureQueryData(
                creatorbaseQueries.posts.options.listAll(getProjectDetailsPostsParams(campaignId))
            )) as creatorbase.TiktokPost[];

            const tiktokUserIds = getTiktokUserIds(posts);

            const [users, images, stats] = await Promise.all([
                Promise.all(
                    buildUserBatchParams(tiktokUserIds).map((params) =>
                        queryClient.ensureQueryData(tiktokQueries.tiktokUsers.options.list(params))
                    )
                ),
                Promise.all(
                    buildUserImageBatchParams(tiktokUserIds).map((params) =>
                        queryClient.ensureQueryData(tiktokQueries.tiktokUserImages.options.list(params))
                    )
                ),
                Promise.all(
                    buildUserStatsCacheBatchParams(tiktokUserIds).map((params) =>
                        queryClient.ensureQueryData(tiktokQueries.tiktokUserStatsCache.options.list(params))
                    )
                ),
            ]);

            const usersById = new Map(users.flatMap((result) => result.results).map((user) => [user.id, user]));
            const imagesByUserId = new Map(images.flat().map((image) => [image.user_id, image]));
            const statsByUserId = new Map(
                stats.flatMap((result) => result.results).map((stat) => [stat.user_id, stat])
            );

            return assembleRows(posts, usersById, imagesByUserId, statsByUserId);
        },
        [queryClient]
    );
}
